import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  HStack,
} from "@chakra-ui/react";
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  createUpdateCommitDataRow,
  InternalIntegrationTestingUpdateNotesFormTable,
} from "features/internal/integration-testing/InternalIntegrationTestingUpdateNotesFormTable";
import { getInternalIntegrationTestingQueueTableQueryKey } from "gen/endpoints/integration-testing-queue/integration-testing-queue";
import {
  internalIntegrationTestingReportCustomerNameUpdateOptions,
  internalIntegrationTestingReportTestNameUpdateOptions,
  internalIntegrationTestingReportUpdateTableRetrieve,
  useInternalIntegrationTestingFilesUpdate,
  useInternalIntegrationTestingReportRetrieveSuspense,
} from "gen/endpoints/integration-testing-reports/integration-testing-reports";
import { GenericSelectData } from "gen/model/genericSelectData";
import { IntegrationTestCheckTypeEnum } from "gen/model/integrationTestCheckTypeEnum";
import { IntegrationTestUpdateTypeEnum } from "gen/model/integrationTestUpdateTypeEnum";
import { TestingReportUpdateRequestDataRequest } from "gen/model/testingReportUpdateRequestDataRequest";
import { TFunction } from "i18next";
import { isEmpty } from "lodash-es";
import React, { useCallback, useEffect, useMemo } from "react";
import { useForm, UseFormReturn, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import {
  ContentBlock,
  ContentBlockBody,
  ContentBlockGroup,
  ContentBlockHeader,
  ContentBlockTitle,
} from "shared/components/content/ContentBlock";
import Form from "shared/components/form/Form";
import {
  FormFooter,
  FormFooterActions,
} from "shared/components/form/FormFooter";
import { FormSelect } from "shared/components/input/select/Select";
import { SelectOption } from "shared/components/input/select/SelectOption";
import { FormSimpleSelect } from "shared/components/input/select/SimpleSelect";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { DocumentDownloadLink } from "shared/components/link/DownloadDocumentLink";
import { ActionConfirmationDialog } from "shared/components/modal/ActionConfirmationDialog";
import { useConfirmationDialog } from "shared/components/modal/confirm/ConfirmationDialog";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { defaultDataTableRenderCellValue } from "shared/components/table/data/DataTableCellValueRenderer";
import { RenderCellValueFn } from "shared/components/table/data/DataTableTypes";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { CheckIcon } from "shared/icons/CheckIcon";
import { InfoCircleIcon } from "shared/icons/InfoCircleIcon";
import { XIcon } from "shared/icons/XIcon";
import { rootRoute } from "shared/navigation/Routes";
import { naiveFormatEnumValue } from "shared/utils/formattingUtils";

export type IntegrationTestingFilesUpdateArgs = Parameters<
  ReturnType<typeof useInternalIntegrationTestingFilesUpdate>["mutateAsync"]
>[0];

const DOCUMENT_COLUMN_IDS = [
  "comparison_document",
  "actual_document",
  "expected_document",
];

const ParametersContentBlock: React.FC<{
  integrationTestingReportId: string;
  isV2Test: boolean;
  updateType: IntegrationTestUpdateTypeEnum | null | undefined;
  customerNames: string[] | null | undefined;
  form: UseFormReturn<TestingReportUpdateRequestDataRequest>;
  t: TFunction<"internal", "common">;
}> = ({
  integrationTestingReportId,
  isV2Test,
  updateType,
  customerNames,
  form,
}) => {
  const isPartialUpdate =
    updateType === IntegrationTestUpdateTypeEnum.specific_customers_test_names;

  useEffect(() => {
    if (updateType === IntegrationTestUpdateTypeEnum.all_tests) {
      form.setValue("customer_names", []);
      form.setValue("test_names", []);
    }
  }, [form, updateType]);

  const { data: customerNamesSelectData } = useQuery({
    queryKey: [
      "customer-name-select-options",
      integrationTestingReportId,
      updateType,
      customerNames,
    ],
    queryFn: async () =>
      await internalIntegrationTestingReportCustomerNameUpdateOptions(
        integrationTestingReportId,
        {
          update_type: updateType,
        },
      ),
    retry: false,
    throwOnError: false,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

  const { data: testNamesSelectData } = useQuery({
    queryKey: [
      "test-name-select-options",
      integrationTestingReportId,
      updateType,
      customerNames,
    ],
    queryFn: async () =>
      await internalIntegrationTestingReportTestNameUpdateOptions(
        integrationTestingReportId,
        {
          update_type: updateType,
          customer_names: customerNames,
        },
      ),
    retry: false,
    throwOnError: false,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

  const customerNameSelectOptions = useMemo(
    () =>
      customerNamesSelectData?.map((customerNameOption: GenericSelectData) => (
        <SelectOption
          key={customerNameOption.value}
          value={customerNameOption.value}
        >
          {customerNameOption.rich_value}
        </SelectOption>
      )),
    [customerNamesSelectData],
  );

  const testNameSelectOptions = useMemo(
    () =>
      testNamesSelectData?.map((testNameOption: GenericSelectData) => (
        <SelectOption key={testNameOption.value} value={testNameOption.value}>
          {testNameOption.rich_value}
        </SelectOption>
      )),
    [testNamesSelectData],
  );

  return (
    <ContentBlockGroup>
      <ContentBlock>
        <ContentBlockHeader>
          <ContentBlockTitle>Parameters</ContentBlockTitle>
        </ContentBlockHeader>
        <ContentBlockBody width="500px" variant="form">
          <FormSimpleSelect
            name="update_type"
            title="Update Type"
            isDisabled={isV2Test}
          >
            {Object.entries(IntegrationTestUpdateTypeEnum).map(
              ([key, value]) => (
                <SelectOption key={key} value={value}>
                  {naiveFormatEnumValue(value)}
                </SelectOption>
              ),
            )}
          </FormSimpleSelect>
          <Box mt={4}>
            {isPartialUpdate && (
              <FormSelect
                isMulti={true}
                title="Customer Names"
                name="customer_names"
              >
                {customerNameSelectOptions}
              </FormSelect>
            )}
            {isPartialUpdate && !isEmpty(customerNames) && (
              <FormSelect isMulti={true} title="Test Names" name="test_names">
                {testNameSelectOptions}
              </FormSelect>
            )}
          </Box>
        </ContentBlockBody>
      </ContentBlock>
    </ContentBlockGroup>
  );
};

const IncludedUpdateTable: React.FC<{
  integrationTestingReportId: string;
  updateType: IntegrationTestUpdateTypeEnum | null | undefined;
  customerNames: string[] | null | undefined;
  testNames: string[] | null | undefined;
  renderCellValue: RenderCellValueFn;
}> = ({
  integrationTestingReportId,
  updateType,
  customerNames,
  testNames,
  renderCellValue,
}) => {
  const { data: includedUpdateTableData } = useQuery({
    queryKey: [
      "integration-testing-update-included-view",
      updateType,
      customerNames,
      testNames,
      integrationTestingReportId,
    ],
    queryFn: async () =>
      await internalIntegrationTestingReportUpdateTableRetrieve(
        integrationTestingReportId,
        {
          update_type: updateType,
          customer_names: customerNames,
          test_names: testNames,
        },
        { is_remaining_view: false },
      ),
    enabled: !!updateType,
    retry: false,
    throwOnError: false,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

  return includedUpdateTableData ? (
    <TableBox>
      <TableBoxHeader>
        <TableBoxTitle>Tests included in update</TableBoxTitle>
        <DataTableBoxActions />
      </TableBoxHeader>
      <TableBoxBody>
        <DataTable
          data={includedUpdateTableData}
          renderCellValue={renderCellValue}
        />
      </TableBoxBody>
    </TableBox>
  ) : undefined;
};

const RemainingUpdateTable: React.FC<{
  integrationTestingReportId: string;
  updateType: IntegrationTestUpdateTypeEnum | null | undefined;
  customerNames: string[] | null | undefined;
  testNames: string[] | null | undefined;
  renderCellValue: RenderCellValueFn;
}> = ({
  integrationTestingReportId,
  updateType,
  customerNames,
  testNames,
  renderCellValue,
}) => {
  const { data: remainingUpdateTableData } = useQuery({
    queryKey: [
      "integration-testing-update-remaining-view",
      updateType,
      customerNames,
      testNames,
      integrationTestingReportId,
    ],
    queryFn: async () =>
      await internalIntegrationTestingReportUpdateTableRetrieve(
        integrationTestingReportId,
        {
          update_type: updateType,
          customer_names: customerNames,
          test_names: testNames,
        },
        { is_remaining_view: true },
      ),
    retry: false,
    throwOnError: false,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

  return remainingUpdateTableData ? (
    <TableBox>
      <TableBoxHeader>
        <TableBoxTitle>Tests remaining to update</TableBoxTitle>
        <DataTableBoxActions />
      </TableBoxHeader>
      <TableBoxBody>
        <DataTable
          data={remainingUpdateTableData}
          renderCellValue={renderCellValue}
        />
      </TableBoxBody>
    </TableBox>
  ) : undefined;
};

export const InternalIntegrationTestingUpdatePage: React.FC = () => {
  const { t } = useTranslation(["internal", "common"]);
  const { integrationTestingReportId } = useRequiredStringParams();
  const navigate = useNavigate();

  const queryClient = useQueryClient();
  const { mutateAsync: updateIntegrationTestingFiles } =
    useInternalIntegrationTestingFilesUpdate();

  const { data: testingReportData } =
    useInternalIntegrationTestingReportRetrieveSuspense(
      integrationTestingReportId,
    );

  const isV2Test =
    testingReportData.check_type ===
    IntegrationTestCheckTypeEnum.run_full_suite;
  const form = useForm<TestingReportUpdateRequestDataRequest>({
    defaultValues: {
      metadata: {
        commit_rows: [createUpdateCommitDataRow()],
        notes: "",
        testing_report_id: integrationTestingReportId,
      },
      update_type: isV2Test
        ? IntegrationTestUpdateTypeEnum.specific_customers_test_names
        : undefined,
    },
  });
  const updateType = useWatch({ control: form.control, name: "update_type" });
  const customerNames = useWatch({
    control: form.control,
    name: "customer_names",
  });
  const testNames = useWatch({ control: form.control, name: "test_names" });

  const confirmUpdateIntegrationTestingFiles = useConfirmationDialog(
    ActionConfirmationDialog as ActionConfirmationDialog<IntegrationTestingFilesUpdateArgs>,
    {
      title: t("internal:integrationTesting.updateTestingFiles.title"),
      description: t(
        "internal:integrationTesting.updateTestingFiles.confirmation",
      ),
      successMessage: t(
        "internal:integrationTesting.updateTestingFiles.success",
      ),
      performAction: async ({ testingReportId, data }) => {
        await updateIntegrationTestingFiles({ testingReportId, data });
        await queryClient.invalidateQueries({
          queryKey: getInternalIntegrationTestingQueueTableQueryKey(),
        });
      },
    },
  );

  const renderCellValue = useCallback<RenderCellValueFn>(
    (props, fieldDisabled, params) => {
      const { columnContext, cell } = props;
      if (columnContext.columnId == "passed" && cell.value?.type == "boolean") {
        return cell.value.value ? (
          <CheckIcon color="primary.500" />
        ) : (
          <XIcon color="error.500" />
        );
      } else if (
        columnContext.columnId == "test_number" &&
        cell.value?.type == "integer"
      ) {
        return <div>{cell.value.value}</div>;
      } else if (
        DOCUMENT_COLUMN_IDS.includes(columnContext.columnId) &&
        cell.value?.type == "text"
      ) {
        return cell.value.text ? (
          <DocumentDownloadLink documentId={cell.value.text}>
            <div>{t("common:download")}</div>
          </DocumentDownloadLink>
        ) : undefined;
      }
      return defaultDataTableRenderCellValue(props, fieldDisabled, params);
    },
    [t],
  );

  const onSubmit = async (data: TestingReportUpdateRequestDataRequest) => {
    await confirmUpdateIntegrationTestingFiles({
      testingReportId: integrationTestingReportId,
      data,
    });
  };

  return (
    <PageContent>
      <PageHeader
        title={t("internal:integrationTesting.updateTestingFiles.title")}
        helperText={t("internal:integrationTestingReportHelpText", {
          createdAt: testingReportData.created_at,
        })}
        badge={
          <HStack spacing="8px">
            <Badge colorScheme={testingReportData.check_type_badge.color}>
              {testingReportData.check_type_badge.text}
            </Badge>
            <Badge colorScheme={testingReportData.status_badge.color}>
              {testingReportData.status_badge.text}
            </Badge>
          </HStack>
        }
        backButton={{
          title: t("internal:integrationTestingReport"),
          path: rootRoute({})
            .internal({})
            .integrationTesting({})
            .report({ integrationTestingReportId }).$,
        }}
      />
      <PageBody>
        <Form
          form={form}
          onSubmit={onSubmit}
          onSuccess={() =>
            navigate(rootRoute({}).internal({}).integrationTesting({}).$)
          }
        >
          <Alert
            data-ls-enabled={true}
            data-public={true}
            status="warning"
            mt="13px"
            mb="13px"
          >
            <AlertIcon>
              <InfoCircleIcon />
            </AlertIcon>
            <AlertDescription>
              {t("internal:integrationTesting.updateTestingFiles.alert")}
            </AlertDescription>
          </Alert>
          <ParametersContentBlock
            integrationTestingReportId={integrationTestingReportId}
            isV2Test={isV2Test}
            form={form}
            t={t}
            updateType={updateType}
            customerNames={customerNames}
          />
          <IncludedUpdateTable
            integrationTestingReportId={integrationTestingReportId}
            updateType={updateType}
            customerNames={customerNames}
            testNames={testNames}
            renderCellValue={renderCellValue}
          />
          <RemainingUpdateTable
            integrationTestingReportId={integrationTestingReportId}
            updateType={updateType}
            customerNames={customerNames}
            testNames={testNames}
            renderCellValue={renderCellValue}
          />
          <InternalIntegrationTestingUpdateNotesFormTable
            control={form.control}
            t={t}
          />
          <FormFooter>
            <FormFooterActions>
              <FormCancelButton
                onClick={() => {
                  navigate(
                    rootRoute({})
                      .internal({})
                      .integrationTesting({})
                      .report({ integrationTestingReportId }).$,
                  );
                }}
              >
                {t("common:cancel")}
              </FormCancelButton>
              <FormSubmitButton colorScheme="destructive">
                {t("common:submit")}
              </FormSubmitButton>
            </FormFooterActions>
          </FormFooter>
        </Form>
      </PageBody>
    </PageContent>
  );
};
